import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { AppShell } from '@/app/layouts/app-shell'
import { AuthLayout } from '@/features/auth/layouts/auth-layout'
import { ForbiddenPage } from '@/app/pages/forbidden'
import { RequireAccess } from '@/shared/rbac'
import { HomeRedirect } from '@/shared/components/home-redirect'
import { PageSkeleton } from '@/shared/components/patterns'

import { QueryResponsePage } from '@/features/public/pages/query-response'

/** Lazy-loaded feature chunks */
const WorkspaceRoutes = lazy(() => import('@/features/workspace/routes'))
const AuthRoutes = lazy(() => import('@/features/auth/routes'))
const ClientAdminRoutes = lazy(() => import('@/features/client-admin/routes'))
const EngineAdminRoutes = lazy(() => import('@/features/engine-admin/routes'))
const OperationsDashboardRoutes = lazy(() => import('@/features/operations-dashboard/routes'))
const SystemAdminRoutes = lazy(() => import('@/features/system-admin/routes'))
const NotificationsRoutes = lazy(() => import('@/features/notifications/routes'))
const SettingsRoutes = lazy(() => import('@/features/settings/routes'))
const KbRoutes = lazy(() => import('@/features/kb/routes'))
const SearchRoutes = lazy(() => import('@/features/search/routes'))
const ClientsRoutes = lazy(() => import('@/features/clients/routes'))
const PromptConfigRoutes = lazy(() => import('@/features/prompt-config/routes'))
const PlatformConfigRoutes = lazy(() => import('@/features/platform-config/routes'))
const ClientConfigRoutes = lazy(() => import('@/features/client-config/routes'))
const AnnouncementsRoutes = lazy(() => import('@/features/announcements/routes'))
const AccessRoutes = lazy(() => import('@/features/access/routes'))
const McpBookRoutes = lazy(() => import('@/features/mcp-book/routes'))

function LazyFallback() {
  return (
    <div className="flex items-center justify-center py-20">
      <div
        className="h-8 w-8 animate-spin rounded-full border-[3px] border-t-transparent"
        style={{ borderColor: 'var(--primary-200)', borderTopColor: 'transparent' }}
      />
    </div>
  )
}


export function AppRouter() {
  return (
    <Routes>
      {/* Auth routes — no app shell, uses AuthLayout */}
      <Route element={<AuthLayout />}>
        <Route path="auth/*" element={<Suspense fallback={<LazyFallback />}><AuthRoutes /></Suspense>} />
      </Route>

      {/* Error pages — standalone, no layout */}
      <Route path="403" element={<ForbiddenPage />} />
      <Route path="404" element={<Navigate to="/" replace />} />

      {/* Public off-platform provider response page (slice 10) — outside auth wrapper */}
      <Route path="q/:token" element={<QueryResponsePage />} />

      {/* Protected routes — AppShell with sidebar + header */}
      <Route element={<AppShell />}>
        {/* Role-aware home redirect */}
        <Route index element={<HomeRedirect />} />

        {/* Workspace — Medical Coder + Super Admin */}
        <Route
          path="workspace/*"
          element={
            <RequireAccess gate="canAccessWorkspace">
              <Suspense fallback={<LazyFallback />}><WorkspaceRoutes /></Suspense>
            </RequireAccess>
          }
        />

        {/* Client Admin — Client Admin + Super Admin */}
        <Route
          path="client-admin/*"
          element={
            <RequireAccess gate="canAdminClient">
              <Suspense fallback={<LazyFallback />}><ClientAdminRoutes /></Suspense>
            </RequireAccess>
          }
        />

        {/* Engine Admin — Engine Admin + Super Admin */}
        <Route
          path="engine-admin/*"
          element={
            <RequireAccess gate="canAdminEngine">
              <Suspense fallback={<LazyFallback />}><EngineAdminRoutes /></Suspense>
            </RequireAccess>
          }
        />

        {/* Operations Dashboard — Super Admin (pipeline run-health) */}
        <Route
          path="operations-dashboard/*"
          element={
            <RequireAccess gate="canViewOperations">
              <Suspense fallback={<LazyFallback />}><OperationsDashboardRoutes /></Suspense>
            </RequireAccess>
          }
        />


        {/* System Admin — Super Admin only */}
        <Route
          path="system-admin/*"
          element={
            <RequireAccess gate="canAdminSystem">
              <Suspense fallback={<LazyFallback />}><SystemAdminRoutes /></Suspense>
            </RequireAccess>
          }
        />

        {/* Platform Configuration (Mandatory Fields) — gated on its own module so
            the route matches the sidebar link (any role holding mandatory_fields:read). */}
        <Route
          path="platform-config/*"
          element={
            <RequireAccess when={(a) => a.can('mandatory_fields', 'read')}>
              <Suspense fallback={<LazyFallback />}><PlatformConfigRoutes /></Suspense>
            </RequireAccess>
          }
        />

        {/* Clients (Utility) — consolidates client → facilities/providers/users into a single browsing experience */}
        <Route
          path="clients/*"
          element={
            <RequireAccess gate="canViewClients">
              <Suspense fallback={<LazyFallback />}><ClientsRoutes /></Suspense>
            </RequireAccess>
          }
        />

        {/* Prompt Config — Coding Manager + Engine Admin */}
        <Route
          path="prompt-config/*"
          element={
            <RequireAccess gate="canConfigurePrompts">
              <Suspense fallback={<LazyFallback />}><PromptConfigRoutes /></Suspense>
            </RequireAccess>
          }
        />

        {/* MCP Playbook — tabbed docs for the internal MCP (overview + one tab per
            domain). Gated on the same `mcp` module that gates using the MCP. */}
        <Route
          path="mcp-book/*"
          element={
            <RequireAccess when={(a) => a.can('mcp', 'read')}>
              <Suspense fallback={<LazyFallback />}><McpBookRoutes /></Suspense>
            </RequireAccess>
          }
        />

        {/* Client — Module Registry + Client Configuration. Engine Admin + Super Admin. */}
        <Route
          path="client-config/*"
          element={
            <RequireAccess gate="canConfigureClient">
              <Suspense fallback={<LazyFallback />}><ClientConfigRoutes /></Suspense>
            </RequireAccess>
          }
        />

        {/* Updates & Announcements — gated by canViewAnnouncements (updates read +
            the announcements_module feature flag). Hidden entirely when the flag is off. */}
        <Route
          path="announcements/*"
          element={
            <RequireAccess gate="canViewAnnouncements">
              <Suspense fallback={<LazyFallback />}><AnnouncementsRoutes /></Suspense>
            </RequireAccess>
          }
        />

        {/* Legacy redirect: the announcements route was renamed /updates → /announcements.
            Keep old bookmarks/links working (subpaths collapse to the hub). */}
        <Route path="updates" element={<Navigate to="/announcements" replace />} />
        <Route path="updates/*" element={<Navigate to="/announcements" replace />} />

        {/* Access Request & Break-Glass — all authenticated users hold access_request */}
        <Route
          path="access/*"
          element={
            <RequireAccess when={(a) => a.can('access_request', 'read')}>
              <Suspense fallback={<LazyFallback />}><AccessRoutes /></Suspense>
            </RequireAccess>
          }
        />

        {/* Search, Knowledge Base, Notifications & Settings — all authenticated users */}
        <Route path="search/*" element={<Suspense fallback={<LazyFallback />}><SearchRoutes /></Suspense>} />
        {/* KB uses the same page-shaped skeleton as its inner page-chunk
            Suspense (features/kb/routes.tsx PageFallback), so the two loading
            stages render identically instead of spinner → skeleton. */}
        <Route path="kb/*" element={<Suspense fallback={<PageSkeleton />}><KbRoutes /></Suspense>} />
        <Route path="notifications/*" element={<Suspense fallback={<LazyFallback />}><NotificationsRoutes /></Suspense>} />
        <Route path="settings/*" element={<Suspense fallback={<LazyFallback />}><SettingsRoutes /></Suspense>} />
      </Route>

      {/* Catch-all — unknown paths resolve through the role-aware root:
          logged-in users land on their own home (HomeRedirect), logged-out
          users hit the AppShell guard and bounce to /auth/login. Sending them
          straight to /workspace/queue instead would 403 any role without
          workspace access (e.g. engine-admin-only). */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
